import type { TagTone } from '../Badge/Badge';
import { cx } from '../cx';
import { Drawing } from './Drawing';
import type { DrawingName } from './drawings';

/**
 * THE ONE STATUS MARK (redesign register A8 set A, A5-2 "filled for status", L2, L6; brief
 * FE-FOUNDATION section 2; DESIGN.md B1, E3, E4).
 *
 * Ben, Artur and Eugen read the same marks on every page: a red octagon means act now, an
 * orange clock means late, a yellow triangle means keep an eye on, a green tick means done or
 * fine. Each colour has its own SHAPE, so the mark still reads for a person who cannot tell
 * the colours apart (rules R30, R60), and it always stands beside its word (L2: symbol plus
 * word, no capsule). It replaces the mockups' coloured dots on the five pages.
 *
 * A FIFTH ROLE, "WAITING ON CLIENT" (register A6-10, decision page item 5.1 = B, picked by rule
 * on 5 October 2026 under A7-N): a late step the client has to act on drew the same orange clock
 * as one we are late on. It is now a purple person with a clock, our drawing of Apple's
 * person.badge.clock.fill: its own outline against the clock, and System Purple, the system
 * colour furthest in hue from the four ladder colours, from interactive blue and from cyan.
 *
 * THE OPTION DOT LIVES HERE TOO (BR-PARTS, B1 "one part per visual thing"). The rest of the CRM
 * marks a select option's colour with Finder's tag circle (`tone`), which `fields/display/TagDot`
 * and `app/fulfillment/clients/HealthMark` drew as copies of their own. Both now render this
 * part and draw exactly what they drew; which of those dots become the four status shapes is the
 * rest-of-CRM round's call (DESIGN.md C5 and Part 6), not this part's.
 *
 * TWO SIZES, ONE DRAWING (E4: "draw legend and cell from one symbol component in two sizes").
 * `text` follows the word beside it: one factor per context, `f = P / 20.48` with P the font
 * size of the word (README "Sizing"), written in `em`, so a 13px meta line gets a 13px-point
 * symbol and a 15px row a 15px one. `legend` is the symbol page's small size, `f = 2/3`, the
 * 16px circle (README "Sizing"), whatever the legend's text size. Both stand on the word's
 * x-height line: `vertical-align: middle` puts the box's middle on the baseline plus half the
 * x-height, which is where a lower-case word's middle is. That holds in INLINE flow beside the
 * word; a flex row centres boxes on its line box instead, so a legend entry keeps the symbol and
 * its word as inline content of one span.
 *
 * DECORATIVE BESIDE ITS WORD, NAMED WITHOUT ONE. When the word stands next to it the symbol
 * would only say the word twice to a screen reader, so it is hidden; a symbol on its own
 * (a table cell that prints no word) is `role="img"` named by the word it stands for. It has
 * no hit box: it is never a control, and a cell that is one draws its own box around it.
 */
export type StatusRole = 'act-now' | 'late' | 'waiting' | 'watch' | 'done';

export const STATUS_ROLES: readonly StatusRole[] = ['act-now', 'late', 'waiting', 'watch', 'done'];

/** The drawing per role, from `design-system/symbols/manifest.json` roles. */
export const STATUS_DRAWING: Readonly<Record<StatusRole, DrawingName>> = {
  'act-now': 'actNow',
  late: 'late',
  waiting: 'waiting',
  watch: 'watch',
  done: 'done',
};

/** The role token per role (tokens.css, fe-foundation block). */
export const STATUS_INK: Readonly<Record<StatusRole, string>> = {
  'act-now': 'text-(--color-status-act-now)',
  late: 'text-(--color-status-late)',
  waiting: 'text-(--color-status-waiting)',
  watch: 'text-(--color-status-watch)',
  done: 'text-(--color-status-done)',
};

/** README "Sizing": an Apple point size P is `f = P / 20.48` in our common scale. */
export const POINT_FACTOR = 1 / 20.48;

/** README "Sizing": `f = 2/3` is the 16px size, the symbol page's small size for a legend. */
export const LEGEND_FACTOR = 2 / 3;

export type StatusSymbolSize = 'text' | 'legend';

/**
 * A SELECT OPTION'S COLOUR, AS FINDER'S TAG CIRCLE (P4 round 2, finding F1; moved here from
 * `fields/display/TagDot`). The fill is the semantic role, not the tag pair: `--color-tag-*` is
 * a capsule's dark text tone and reads muddy as a fill, so a dot takes the product's own colour
 * role, and neutral takes the subtle ink Finder gives its colourless tag. It keeps its colour on
 * a selected row, where the row repoints only the ink tokens. The diameter is `--list-tag-dot`
 * (no kit master; see the token's own note).
 */
const DOT_TONES: Readonly<Record<TagTone, string>> = {
  neutral: 'bg-ink-subtle',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
};

interface StatusSymbolCommon {
  /**
   * The word it stands for, when no word is printed beside it. Absent (the usual case) makes
   * the symbol decorative, because the word beside it already says it. A dot is always
   * decorative: it is the second encoding of the option label printed beside it.
   */
  readonly label?: string | undefined;
  /** `text` (default) follows the word's size; `legend` is the 16px legend size. */
  readonly size?: StatusSymbolSize | undefined;
  readonly className?: string | undefined;
}

export type StatusSymbolProps = StatusSymbolCommon &
  (
    | {
        /** Which of the five (named `status`, not `role`, which is an ARIA attribute). */
        readonly status: StatusRole;
        readonly tone?: undefined;
      }
    | {
        /** An option colour, drawn as the tag circle the rest of the CRM uses. */
        readonly tone: TagTone;
        readonly status?: undefined;
      }
  );

export function StatusSymbol({ status, tone, label, size = 'text', className }: StatusSymbolProps) {
  if (tone !== undefined) {
    return <span aria-hidden="true" className={cx('list-tag-dot', DOT_TONES[tone], className)} />;
  }
  return (
    <Drawing
      name={STATUS_DRAWING[status]}
      factor={size === 'legend' ? LEGEND_FACTOR : POINT_FACTOR}
      unit={size === 'legend' ? 'px' : 'em'}
      label={label}
      className={cx('align-middle', STATUS_INK[status], className)}
      sizeName={size}
    />
  );
}
